import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Calendar, Activity, AlertTriangle, Plus, Weight } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import toast from 'react-hot-toast'
import api from '../../services/api'

export default function PortalDashboardPage() {
    const { token } = useParams()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)

    const [showWeightInput, setShowWeightInput] = useState(false)
    const [newWeight, setNewWeight] = useState('')

    const fetchData = async () => {
        try {
            const res = await api.get(`/api/portal/${token}/dashboard`)
            setData(res.data)
        } catch (err) {
            setError(err.response?.data?.detail || "Lien expiré ou invalide.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchData() }, [token])

    const handleAddWeight = async (e) => {
        e.preventDefault()
        if (!newWeight) return
        try {
            await api.post(`/api/portal/${token}/weight`, { weight: newWeight })
            toast.success("Poids enregistré avec succès !")
            setShowWeightInput(false)
            setNewWeight('')
            fetchData() // On recharge le dashboard pour actualiser le graphique !
        } catch (e) {
            toast.error("Erreur lors de l'enregistrement")
        }
    }


    if (loading) return <div className="h-full flex items-center justify-center"><div className="spinner border-sage-600"/></div>

    if (error) return (
        <div className="h-full flex flex-col items-center justify-center text-center">
            <AlertTriangle className="w-16 h-16 text-red-400 mb-4 opacity-50" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Accès refusé</h2>
            <p className="text-gray-500">{error}</p>
        </div>
    )

    const aptDate = data.next_appointment ? new Date(data.next_appointment.date) : null

    // On vérifie si on est le jour J
    const todayStr = new Date().toISOString().split('T')[0]
    const isAptToday = data.next_appointment && data.next_appointment.date === todayStr

    return (
        <div className="space-y-6 animate-in fade-in">
            {/* Bonjour */}
            <div>
                <h1 className="text-2xl font-serif text-sage-800">Bonjour {data.patient.first_name} ! 👋</h1>
                <p className="text-gray-500 text-sm">Bienvenue sur votre espace personnel.</p>
            </div>

            {/* Prochain RDV */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-sage-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-sage-400"></div>
                <h3 className="text-xs font-bold text-sage-600 uppercase mb-3 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4"/> Prochain rendez-vous
                </h3>
                
                {data.next_appointment ? (
                    <div>
                        <p className="text-2xl font-black text-gray-800">
                            {aptDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                        <p className="text-lg text-sage-700 font-medium">
                            à {data.next_appointment.start_time}
                        </p>
                        
                        {/* BOUTON DYNAMIQUE (Visio) */}
                        {data.next_appointment.type === 'video' ? (
                            isAptToday ? (
                                <button 
                                    onClick={() => navigate(`/p/${token}/visio?room=${data.next_appointment.token}`)}
                                    className="mt-4 w-full bg-purple-600 text-white font-bold py-3 px-4 rounded-xl shadow-md hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 animate-in zoom-in"
                                >
                                    🎥 Rejoindre la salle d'attente
                                </button>
                            ) : (
                                <span className="inline-block mt-3 px-3 py-1.5 bg-purple-50 text-purple-700 text-xs font-bold rounded-lg uppercase">
                                    🎥 Vidéo ( Accès disponible le jour J )
                                </span>
                            )
                        ) : (
                            <span className="inline-block mt-3 px-3 py-1 bg-sage-50 text-sage-700 text-xs font-bold rounded-lg uppercase">
                                🏥 Consultation au Cabinet
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-sm text-gray-500 mb-4">Aucun rendez-vous planifié à ce jour.</p>
                        <button 
                            onClick={() => navigate(`/p/${token}/booking`)} 
                            className="btn btn-primary w-full shadow-sm"
                        >
                            Prendre un nouveau rendez-vous
                        </button>
                    </div>
                )}
            </div>

            {/* Suivi du Poids (Masquage dynamique de la carte et du graphique anti-TCA) */}
            {(data?.features?.weight !== false) && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5">
                            <Activity className="w-4 h-4"/> Évolution du Poids
                        </h3>
                        <button 
                            onClick={() => setShowWeightInput(!showWeightInput)}
                            className="w-8 h-8 rounded-full bg-sage-50 text-sage-600 flex items-center justify-center hover:bg-sage-100 transition-colors"
                        >
                            <Plus className="w-4 h-4"/>
                        </button>
                    </div>

                    {showWeightInput && (
                        <form onSubmit={handleAddWeight} className="mb-6 flex gap-2 animate-in zoom-in-95">
                            <div className="relative flex-1">
                                <input 
                                    type="number" step="0.1" autoFocus
                                    value={newWeight} onChange={e => setNewWeight(e.target.value)}
                                    placeholder="Votre poids..." 
                                    className="w-full pl-9 py-2 text-sm bg-gray-50 border-gray-200 rounded-xl"
                                />
                                <Weight className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            </div>
                            <button type="submit" className="btn btn-primary py-2 px-4 text-sm shadow-md">Valider</button>
                        </form>
                    )}

                    {data.weight_history && data.weight_history.length > 0 ? (
                        <div className="h-48 w-full mt-4">
                            <ResponsiveContainer width="99%" height="100%">
                                {(() => {
                                    const chartData = Object.values(data.weight_history.reduce((acc, m) => {
                                        acc[m.date] = m;
                                        return acc;
                                    }, { })); // attention espace

                                    return (
                                        <LineChart data={chartData}>
                                            <XAxis dataKey="date" hide />
                                            <YAxis domain={['auto', 'auto']} hide />
                                            <Tooltip 
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px' }}
                                                formatter={(value) =>[`${value} kg`, "Poids"]}
                                                labelFormatter={(l) => new Date(l).toLocaleDateString('fr-FR')}
                                            />
                                            <Line 
                                                type="monotone" 
                                                dataKey="weight" 
                                                stroke="#7d9668" 
                                                strokeWidth={4} 
                                                dot={{ fill: '#7d9668', strokeWidth: 3, r: 4 }} 
                                                activeDot={{ r: 6 }}
                                            />
                                        </LineChart>
                                    );
                                })()}
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="text-center py-6 text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl">
                            Renseignez votre premier poids pour voir la courbe !
                        </div>
                    )}
                </div>
            )}

        </div>
    )
}
